import { Storage } from '@lightningjs/sdk'
import sjcl from 'sjcl'

import { AppConfig } from '../services/appConfig'
import { Flo } from '../types/flo-auth-api'
import { clearDatadogUser, setDatadogUser } from './datadog'
import { deleteWatchHistory } from './watchHistory'

const FloUserData = 'floUserData'

export const storeUserData = (data: Flo.Auth.LoginResponse) => {
  setDatadogUser(data)

  Storage.set(FloUserData, data)
}

export const getUserData = (): Flo.Auth.LoginResponse => {
  return Storage.get(FloUserData)
}

export const isUserSubscriptionActive = (): boolean => {
  const userData = Storage.get(FloUserData)

  if (userData) {
    const data = userData as Flo.Auth.LoginResponse

    return data?.user?.user_subscriptions?.[0]?.is_active
  }

  return false
}

export const hasActiveSubscription = (
  subscription: Flo.Auth.UserSubscription | Flo.Payments.Subscription,
): boolean => {
  return subscription?.status?.toLowerCase() === 'active'
}

export const getAuthToken = () => {
  const data = getUserData()
  return data?.token
}

export const getRefreshToken = () => {
  const data = getUserData()
  return data?.refresh_token
}

export const getTokenExpTime = () => {
  const data = getUserData()

  const expTimestamp = data?.exp
  const expDate = new Date(expTimestamp * 1000) // Convert seconds to milliseconds

  return expDate
}

export const isTokenExpired = () => {
  const tokenExpDate = getTokenExpTime()
  const currentDate = new Date()

  return currentDate >= tokenExpDate
}

export const isUserSignedIn = () => {
  const data = getUserData()
  return data && data?.token
}

export const deleteUserData = () => {
  clearDatadogUser()
  Storage.remove(FloUserData)
  deleteWatchHistory()
}

export const generateHmac = (data, securityKey) => {
  const key = sjcl.codec.utf8String.toBits(securityKey)
  const encrypted = new sjcl.misc.hmac(key, sjcl.hash.sha256).encrypt(data)
  return sjcl.codec.base64.fromBits(encrypted)
}

export const convertSubscriptionIntoLegacy = (
  subscription: Flo.Payments.Subscription,
): Flo.Auth.UserSubscription => {
  const legacySubscription = getUserData()?.user?.user_subscriptions?.[0]
  const oldestTime = new Date(0)
  const subscriptionDate = subscription?.accessEndDate || oldestTime
  const legacySubscriptionDate = legacySubscription?.end_date || oldestTime
  const offer_type = subscription?.offers?.[0]?.type

  return {
    status: subscription?.status,
    is_active:
      subscription?.status?.toLowerCase() === 'active' || subscriptionDate > legacySubscriptionDate,
    type: subscription?.type,
    id: subscription?.id,
    created_at: subscription?.createdAt,
    end_date: subscription?.accessEndDate,
    modified_at: subscription?.updatedAt,
    offer_type,
  }
}

export const updateUserSubscriptionData = async (subscription?: Flo.Payments.Subscription) => {
  const data = getUserData()

  if (!subscription) {
    subscription = await AppConfig.shared.authService.getUserSubscription()
  }

  if (subscription) {
    data.user.user_subscriptions = [convertSubscriptionIntoLegacy(subscription)]
  }

  storeUserData(data)
}
